import { useQuery } from '@tanstack/react-query';
import { chatApi } from '../services/api/chatApi';
import { queryKeys } from './queryKeys';

export type ChatScope = 'issue' | 'finding' | 'clause';

/**
 * Plan 143: Resolve a scoped chat session by (matter, scope, sourceId).
 *
 * Fetches the existing session if present; creates it lazily on first use.
 * Server is the source of truth — deskStore no longer caches the mapping, so
 * reload recovers the real session id and avoids the duplicate-session bug.
 *
 * Create is idempotent on the backend (unique-violation catch), so the
 * get-then-create sequence is race-safe.
 */
export function useScopedChatSession(
  matterId: string | null,
  scope: ChatScope,
  sourceId: string | null,
  displayName: string,
) {
  const query = useQuery({
    queryKey:
      matterId && sourceId
        ? queryKeys.matters.scopedChatSession(matterId, scope, sourceId)
        : (['scopedChatSession', 'idle'] as const),
    enabled: !!matterId && !!sourceId,
    queryFn: async () => {
      if (!matterId || !sourceId) return null;

      const existing = await chatApi.getChatSessionByScope(matterId, scope, sourceId);
      if (existing) return existing;

      return chatApi.createMatterChatSession(matterId, displayName, scope, sourceId);
    },
    staleTime: Infinity,
  });

  return {
    sessionId: query.data?.id ?? null,
    isLoading: query.isLoading,
    error: (query.error as Error | null) ?? null,
  };
}
